import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Mark, mergeAttributes } from "@tiptap/core";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Quote,
  Link2,
  Heading2,
  Heading3,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Undo2,
  Redo2,
  Type,
} from "lucide-react";

/**
 * Custom mark: rajattu fonttivalitsija (whitelist).
 * Tallentaa <span data-font="display|body|script">...</span>.
 * CSS-säännöt määrittelevät fontit – käyttäjä ei voi syöttää vapaata fonttia.
 */
const FontChoice = Mark.create({
  name: "fontChoice",
  addOptions() { return { types: ["display", "body", "script"] as const }; },
  addAttributes() {
    return {
      font: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-font"),
        renderHTML: (attrs) => (attrs.font ? { "data-font": attrs.font } : {}),
      },
    };
  },
  parseHTML() { return [{ tag: "span[data-font]" }]; },
  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes), 0];
  },
  addCommands() {
    return {
      setFontChoice:
        (font: "display" | "body" | "script" | null) =>
        ({ chain }: any) =>
          font === null
            ? chain().unsetMark("fontChoice").run()
            : chain().setMark("fontChoice", { font }).run(),
    } as any;
  },
});

const ToolbarButton = ({
  onClick,
  active,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  title: string;
  children: React.ReactNode;
}) => (
  <Button
    type="button"
    size="sm"
    variant={active ? "default" : "ghost"}
    title={title}
    onClick={onClick}
    className="h-8 w-8 p-0"
  >
    {children}
  </Button>
);

const Toolbar = ({ editor }: { editor: Editor }) => {
  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Linkin osoite (esim. https://...)", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const currentFont = editor.getAttributes("fontChoice").font ?? "";

  return (
    <div className="flex flex-wrap items-center gap-1 rounded-t-md border border-b-0 bg-muted/40 p-1.5">
      <ToolbarButton title="Lihavointi" active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton title="Kursivointi" active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton title="Alleviivaus" active={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon className="h-4 w-4" /></ToolbarButton>

      <span className="mx-1 h-5 w-px bg-border" />

      <ToolbarButton title="Väliotsikko (iso)" active={editor.isActive("heading", { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton title="Väliotsikko (pieni)" active={editor.isActive("heading", { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></ToolbarButton>

      <span className="mx-1 h-5 w-px bg-border" />

      <ToolbarButton title="Luettelo" active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton title="Numerolista" active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton title="Sitaatti" active={editor.isActive("blockquote")}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton title="Linkki" active={editor.isActive("link")} onClick={setLink}>
        <Link2 className="h-4 w-4" />
      </ToolbarButton>

      <span className="mx-1 h-5 w-px bg-border" />

      <ToolbarButton title="Tasaa vasemmalle" active={editor.isActive({ textAlign: "left" })}
        onClick={() => editor.chain().focus().setTextAlign("left").run()}><AlignLeft className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton title="Keskitä" active={editor.isActive({ textAlign: "center" })}
        onClick={() => editor.chain().focus().setTextAlign("center").run()}><AlignCenter className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton title="Tasaa oikealle" active={editor.isActive({ textAlign: "right" })}
        onClick={() => editor.chain().focus().setTextAlign("right").run()}><AlignRight className="h-4 w-4" /></ToolbarButton>

      <span className="mx-1 h-5 w-px bg-border" />

      <div className="flex items-center gap-1">
        <Type className="h-4 w-4 text-muted-foreground" />
        <select
          aria-label="Fontti"
          value={currentFont}
          onChange={(e) => {
            const v = e.target.value;
            (editor as any).chain().focus().setFontChoice(v === "" ? null : v).run();
          }}
          className="h-8 rounded-md border bg-background px-2 text-xs"
        >
          <option value="">Oletusfontti</option>
          <option value="display">Klassinen otsikko (Playfair)</option>
          <option value="body">Leipäteksti (Inter)</option>
          <option value="script">Käsiala (Caveat)</option>
        </select>
      </div>

      <span className="ml-auto" />

      <ToolbarButton title="Kumoa" onClick={() => editor.chain().focus().undo().run()}>
        <Undo2 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton title="Tee uudelleen" onClick={() => editor.chain().focus().redo().run()}>
        <Redo2 className="h-4 w-4" />
      </ToolbarButton>
    </div>
  );
};

export const RichEditor = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (html: string) => void;
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      TextStyle,
      FontChoice,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "rich-content min-h-[140px] w-full rounded-b-md border bg-background p-4 focus:outline-none focus:ring-2 focus:ring-ring",
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Sync external value changes (e.g. when data loads)
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) editor.commands.setContent(value || "", { emitUpdate: false });
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="overflow-hidden rounded-md">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
};
